import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { SECTION_ICON_STYLE } from '../SectionCard/sectionIconStyle';
import { useParams, NavLink } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEnvelope, faPhone, faIdCard, faShieldAlt,
    faMapMarkerAlt, faUser, faBirthdayCake, faCircleCheck,
    faHourglassStart, faCoins, faBuilding, faCircleInfo,
    faEllipsisH, faEye, faFileInvoice, faRotateLeft, faBan, faTrash, faCamera,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { eventBus } from '../../bus/eventBus';
import InlineLoader from '../Loader/InlineLoader';
import { formatPrice } from '../../utils/FormatPrice';
import Table from '../Listing/Table';
import PaymentMethodBadge from '../Order/PaymentMethodBadge';
import NewInvoiceModal from '../Modal/NewInvoiceModal';
import CreateReturnModal from '../Modal/CreateReturnModal';
import ConfirmModal from '../Modal/ConfirmModal';
import PatientDebtModal from '../Modal/PatientDebtModal';
import { useAuth } from '../../context/AuthProvider';
import { API_BASE_URL } from '../../api';
import './PatientDashboard.scss';

// Leaflet lives in its own chunk, fetched only once the map scrolls into view
const AddressMap = lazy(() => import('./AddressMap'));

const LazyAddressMap = ({ address }) => {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (visible || !ref.current) return;
        if (!('IntersectionObserver' in window)) { setVisible(true); return; }
        const observer = new IntersectionObserver((entries) => {
            if (entries.some(e => e.isIntersecting)) {
                setVisible(true);
                observer.disconnect();
            }
        }, { rootMargin: '200px' });
        observer.observe(ref.current);
        return () => observer.disconnect();
    }, [visible]);

    const placeholder = <div className="pd-map-placeholder" />;
    return (
        <div ref={ref}>
            {visible
                ? <Suspense fallback={placeholder}><AddressMap address={address} /></Suspense>
                : placeholder}
        </div>
    );
};

const stringToColor = (str = '') => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xFF;
        color += (`00${value.toString(16)}`).slice(-2);
    }
    return color;
};

const InfoRow = ({ icon, label, value }) => (
    <div className="pd-info-row">
        <span className="pd-info-icon">
            <FontAwesomeIcon icon={icon} />
        </span>
        <div className="pd-info-body">
            <span className="pd-info-label">{label}</span>
            <span className="pd-info-value">{value || '—'}</span>
        </div>
    </div>
);

const SectionCard = ({ title, icon, children, className = '' }) => (
    <div className={`pd-section${className ? ` ${className}` : ''}`}>
        <div className="pd-section-title">
            {icon && (
                <span className="pd-section-icon" style={SECTION_ICON_STYLE}>
                    <FontAwesomeIcon icon={icon} />
                </span>
            )}
            {title}
        </div>
        {children}
    </div>
);

const ORDER_STATUS = {
    COMPLETE: { label: 'Terminé',   bg: '#ecfdf5', color: '#065f46', dot: '#10b981' },
    PENDING:  { label: 'En attente', bg: '#fffbeb', color: '#92400e', dot: '#f59e0b' },
    CANCELED: { label: 'Annulé',    bg: '#fef2f2', color: '#991b1b', dot: '#ef4444' },
};

const OrderStatusBadge = ({ status }) => {
    const s = ORDER_STATUS[status] ?? ORDER_STATUS.PENDING;
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            background: s.bg, color: s.color,
            padding: '3px 9px', borderRadius: 999,
            fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
        }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />
            {s.label}
        </span>
    );
};

const formatAmount = (val) =>
    new Intl.NumberFormat('fr-MA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val ?? 0);

const getRecentOrdersColumns = ({ onNewInvoice, onCreateReturn, onCancelOrder, canCreateReturn, canCancel, canCreateInvoicePayment }) => [
    {
        Header: 'Référence',
        accessor: 'reference',
        // the only column that grows: the others shrink to their content (width 1% + nowrap)
        width: '100%',
        Cell: (row) => (
            <div>
                <NavLink to={`/order/${row.id}`} className="cell-name" style={{ fontWeight: 600, display: 'block' }}>
                    {row.reference}
                </NavLink>
                <span style={{ fontSize: 12, color: '#9ca3af' }}>
                    {row.createdAt ? new Date(row.createdAt).toLocaleDateString('fr-MA', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                </span>
            </div>
        ),
    },
    {
        Header: 'Statut',
        accessor: 'status',
        className: 'text-nowrap',
        width: '1%',
        Cell: (row) => <OrderStatusBadge status={row.status} />,
    },
    {
        Header: 'Montant',
        accessor: 'total',
        className: 'text-nowrap',
        width: '1%',
        Cell: (row) => (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 3 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{formatAmount(row.total)}</span>
                <span style={{ fontSize: 11, color: '#9ca3af' }}>dhs</span>
            </div>
        ),
    },
    {
        Header: 'Articles',
        accessor: 'items',
        className: 'text-nowrap',
        width: '1%',
        Cell: (row) => (
            <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 26, height: 26, borderRadius: '50%',
                background: '#f3f4f6', fontSize: 12, fontWeight: 700, color: '#374151',
            }}>
                {row.items?.length ?? 0}
            </span>
        ),
    },
    {
        Header: 'Paiement',
        accessor: 'paymentMethods',
        className: 'text-nowrap',
        width: '1%',
        Cell: (row) => {
            const methods = row.paymentMethods;
            if (!methods || methods.length === 0) return <span style={{ color: '#9ca3af', fontSize: 12 }}>—</span>;
            return (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {methods.map((m) => <PaymentMethodBadge key={m} method={m} />)}
                </div>
            );
        },
    },
    {
        Header: '',
        accessor: 'actions',
        className: 'col-actions',
        width: '1%',
        Cell: (row) => (
            <div className="cell-dropdown">
                <button
                    className="cell-actions-btn dropdown-toggle"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                >
                    <FontAwesomeIcon icon={faEllipsisH} />
                </button>
                <ul className="dropdown-menu dropdown-menu-end">
                    <li>
                        <NavLink to={`/order/${row.id}`} className="dropdown-item">
                            <FontAwesomeIcon icon={faEye} className="me-2" />
                            Voir la vente
                        </NavLink>
                    </li>
                    {row.status === 'PENDING' && canCreateInvoicePayment && (
                        <li>
                            <button className="dropdown-item" onClick={() => onNewInvoice(row)}>
                                <FontAwesomeIcon icon={faFileInvoice} className="me-2" />
                                Nouveau paiement
                            </button>
                        </li>
                    )}
                    {row.status !== 'CANCELED' && (canCreateReturn || canCancel) && (
                        <>
                            <li><hr className="dropdown-divider" /></li>
                            {canCreateReturn && (
                                <li>
                                    <button className="dropdown-item" onClick={() => onCreateReturn(row)}>
                                        <FontAwesomeIcon icon={faRotateLeft} className="me-2" />
                                        Créer un retour
                                    </button>
                                </li>
                            )}
                            {canCancel && (
                                <li>
                                    <button className="dropdown-item danger" onClick={() => onCancelOrder(row.id)}>
                                        <FontAwesomeIcon icon={faBan} className="me-2" />
                                        Annuler la vente
                                    </button>
                                </li>
                            )}
                        </>
                    )}
                </ul>
            </div>
        ),
    },
];

const PatientDashboard = () => {
    const { id } = useParams();
    const { hasRight } = useAuth();
    const canCancel = hasRight('ORDERS_CANCEL');
    const canCreateReturn = hasRight('RETURNS_CREATE');
    const canCreateInvoicePayment = hasRight('INVOICES_CREATE') && hasRight('PAYMENTS_CREATE');
    const canEditPatient = hasRight('PATIENTS_EDIT');
    const photoInputRef = useRef(null);
    const [photoUploading, setPhotoUploading] = useState(false);

    // The profile photo is managed from the avatar itself (no field in the Profil form)
    const uploadPhoto = async (file) => {
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            toast.error('Choisissez une image');
            return;
        }
        const formData = new FormData();
        formData.append('photo', file);
        setPhotoUploading(true);
        try {
            const { data } = await axios.post(`${API_BASE_URL}/api/v1/patients/${id}/photo`, formData, { withCredentials: true });
            setPatient(prev => ({ ...prev, photo: data.photo }));
            eventBus.emit('patientPhotoUpdated');
        } catch (err) {
            console.error(err);
            toast.error('Erreur lors de l\'enregistrement de la photo');
        } finally {
            setPhotoUploading(false);
        }
    };

    const removePhoto = async () => {
        try {
            await axios.delete(`${API_BASE_URL}/api/v1/patients/${id}/photo`, { withCredentials: true });
            setPatient(prev => ({ ...prev, photo: null }));
            eventBus.emit('patientPhotoUpdated');
        } catch (err) {
            console.error(err);
            toast.error('Erreur lors de la suppression de la photo');
        }
    };
    const [patient, setPatient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [recentOrders, setRecentOrders] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showNewInvoiceModal, setShowNewInvoiceModal] = useState(false);
    const [showCreateReturnModal, setShowCreateReturnModal] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [cancelIds, setCancelIds] = useState([]);
    const [showDebtModal, setShowDebtModal] = useState(false);

    const handleNewInvoice = (row) => { setSelectedOrder(row); setShowNewInvoiceModal(true); };
    const handleCreateReturn = (row) => { setSelectedOrder(row); setShowCreateReturnModal(true); };
    const handleCancelOrder = (orderId) => { setCancelIds([orderId]); setShowCancelConfirm(true); };
    const handleBulkCancel = (ids) => { setCancelIds(ids); setShowCancelConfirm(true); };
    const handleConfirmCancel = async () => {
        try {
            await Promise.all(cancelIds.map(id =>
                axios.patch(`${API_BASE_URL}/api/v1/orders/${id}/status`, { status: 'CANCELED' }, { withCredentials: true })
            ));
            setRecentOrders(prev => prev.map(o => cancelIds.includes(o.id) ? { ...o, status: 'CANCELED' } : o));
            // Cancelling an order changes the patient's remainingToPay/paymentUpToDate —
            // re-fetch so the hero badge reflects it instead of staying stuck at its old value.
            refreshPatientData();
        } catch (err) {
            console.error('Failed to cancel orders:', err);
        } finally {
            setShowCancelConfirm(false);
            setCancelIds([]);
        }
    };

    const recentOrdersColumns = getRecentOrdersColumns({
        onNewInvoice: handleNewInvoice,
        onCreateReturn: handleCreateReturn,
        onCancelOrder: handleCancelOrder,
        canCreateReturn,
        canCancel,
        canCreateInvoicePayment,
    });

    // Card-view design for this listing (Table's enableCardView feature, forced on mobile)
    // — reuses the actions column's own dropdown so both views offer identical actions.
    const ordersActionsColumn = recentOrdersColumns.find((c) => c.accessor === 'actions');
    const renderOrderCard = (row) => (
        <div className="card h-100 shadow-sm">
            <div className="card-body d-flex flex-column" style={{ padding: 14, gap: 6 }}>
                <div style={{ alignSelf: 'flex-start' }}><OrderStatusBadge status={row.status} /></div>

                <NavLink to={`/order/${row.id}`} style={{ fontSize: 13.5, fontWeight: 600, color: '#111827', marginTop: 4 }}>
                    {row.reference}
                </NavLink>
                <span style={{ fontSize: 11.5, color: '#9ca3af' }}>
                    {row.createdAt ? new Date(row.createdAt).toLocaleDateString('fr-MA', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                </span>

                {row.paymentMethods?.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {row.paymentMethods.map((m) => <PaymentMethodBadge key={m} method={m} />)}
                    </div>
                )}

                <div className="d-flex align-items-center justify-content-between" style={{ marginTop: 'auto', paddingTop: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                        <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{formatAmount(row.total)}</span>
                        <span style={{ fontSize: 10.5, color: '#9ca3af', fontWeight: 500 }}>dhs</span>
                        <span style={{
                            marginLeft: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 22, height: 22, borderRadius: '50%',
                            background: '#f3f4f6', fontSize: 11, fontWeight: 700, color: '#374151',
                        }}>
                            {row.items?.length ?? 0}
                        </span>
                    </div>
                    {ordersActionsColumn?.Cell(row)}
                </div>
            </div>
        </div>
    );

    // Re-fetches the patient + their recent orders — remainingToPay/paymentUpToDate are
    // computed live on the backend from orders/payments, so this is called after any action
    // from this page that can change the balance due: new invoice/payment, return, or order
    // cancellation, instead of leaving the hero badge stuck at its stale initial value.
    const refreshPatientData = useCallback(async ({ showLoader = false } = {}) => {
        try {
            if (showLoader) setLoading(true);
            const [patientRes, ordersRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/api/v1/getPatient/${id}`, { withCredentials: true }),
                axios.get(`${API_BASE_URL}/api/v1/getOrders`, {
                    withCredentials: true,
                    params: { patientId: id, page: 0, size: 5, sortField: 'id', sortDirection: 'desc' },
                }),
            ]);
            setPatient(patientRes.data);
            setRecentOrders(ordersRes.data?.content || []);
        } catch (err) {
            console.error(err);
        } finally {
            if (showLoader) setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        refreshPatientData({ showLoader: true });
    }, [refreshPatientData]);

    if (loading) return <InlineLoader />;
    if (!patient) return null;

    const fullName = patient.fullName || `${patient.firstName} ${patient.lastName}`;
    const initials = `${(patient.firstName || '').charAt(0)}${(patient.lastName || '').charAt(0)}`.toUpperCase();
    const avatarColor = stringToColor(patient.firstName + patient.lastName);
    const upToDate = patient.paymentUpToDate !== false;
    const defaultAddress = patient.addresses?.find(a => a.default) || patient.addresses?.[0];

    const sexLabel = patient.sex == 0 ? 'Homme' : patient.sex == 1 ? 'Femme' : null;

    const formatBirthday = (date) => {
        if (!date) return null;
        return new Date(date).toLocaleDateString('fr-MA', { day: 'numeric', month: 'long', year: 'numeric' });
    };

    return (
        <>
        <div className="pd">

            {/* ── Hero ── */}
            <div className="pd-hero">
                <div className="pd-avatar-wrap">
                    <div
                        className={`pd-avatar${canEditPatient ? ' pd-avatar--editable' : ''}${photoUploading ? ' is-loading' : ''}`}
                        style={{ background: patient.photo ? '#f1f5f9' : avatarColor }}
                        onClick={canEditPatient ? () => photoInputRef.current?.click() : undefined}
                        data-tooltip-id={canEditPatient ? 'tooltip' : undefined}
                        data-tooltip-content={canEditPatient ? (patient.photo ? 'Changer la photo' : 'Ajouter une photo') : undefined}
                    >
                        {patient.photo
                            ? <img src={patient.photo} alt={fullName} />
                            : initials}
                        {canEditPatient && (
                            <span className="pd-avatar-overlay">
                                <FontAwesomeIcon icon={faCamera} />
                            </span>
                        )}
                    </div>
                    {canEditPatient && patient.photo && (
                        <button
                            type="button"
                            className="pd-avatar-remove"
                            onClick={removePhoto}
                            data-tooltip-id="tooltip"
                            data-tooltip-content="Supprimer la photo"
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </button>
                    )}
                    {canEditPatient && (
                        <input
                            ref={photoInputRef}
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={(e) => { uploadPhoto(e.target.files?.[0]); e.target.value = ''; }}
                        />
                    )}
                </div>
                <div className="pd-hero-body">
                    <div className="pd-hero-name">{fullName}</div>
                    <div className="pd-hero-meta">
                        {patient.email && (
                            <span className="pd-hero-chip">
                                <FontAwesomeIcon icon={faEnvelope} />
                                {patient.email}
                            </span>
                        )}
                        {patient.phoneNumber && (
                            <span className="pd-hero-chip">
                                <FontAwesomeIcon icon={faPhone} />
                                {patient.phoneNumber}
                            </span>
                        )}
                        {defaultAddress?.city && (
                            <span className="pd-hero-chip">
                                <FontAwesomeIcon icon={faMapMarkerAlt} />
                                {defaultAddress.city}
                            </span>
                        )}
                    </div>
                </div>
                <div className="pd-hero-status">
                    <span
                        className={`pd-status-badge ${upToDate ? 'pd-status-ok' : 'pd-status-warn'}`}
                        data-tooltip-id="tooltip"
                        data-tooltip-content={upToDate ? 'Paiements à jour' : 'Paiements en retard'}
                    >
                        <FontAwesomeIcon icon={upToDate ? faCircleCheck : faHourglassStart} />
                        <span className="pd-status-badge__text">{upToDate ? 'Paiements à jour' : 'Paiements en retard'}</span>
                    </span>
                    {!upToDate && patient.remainingToPay > 0 && (
                        <div className="pd-remaining">
                            <span className="pd-remaining-label">
                                Reste dû
                                <FontAwesomeIcon
                                    icon={faCircleInfo}
                                    style={{ marginInlineStart: 6, cursor: 'pointer', color: '#9ca3af' }}
                                    data-tooltip-id="tooltip"
                                    data-tooltip-content="D'où vient cette dette ?"
                                    onClick={() => setShowDebtModal(true)}
                                />
                            </span>
                            <span className="pd-remaining-value">{formatPrice(patient.remainingToPay)}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Info grid ── */}
            <div className="pd-grid">

                <SectionCard title="Identité" icon={faUser}>
                    <InfoRow icon={faUser}         label="Sexe"       value={sexLabel} />
                    <InfoRow icon={faBirthdayCake}  label="Naissance"  value={formatBirthday(patient.birthDate)} />
                    <InfoRow icon={faIdCard}        label="CIN"        value={patient.cin} />
                    <InfoRow icon={faIdCard}        label="N° SS"      value={patient.socialSecurityNumber} />
                </SectionCard>

                <SectionCard title="Assurance" icon={faShieldAlt}>
                    <InfoRow icon={faShieldAlt}  label="Régime"        value={patient.regime} />
                    <div className="pd-info-row">
                        <span className="pd-info-icon"><FontAwesomeIcon icon={faBuilding} /></span>
                        <div className="pd-info-body">
                            <span className="pd-info-label">Mutuelle</span>
                            <span className="pd-info-value pd-info-insurance">
                                {patient.healthInsurance ? (
                                    <>
                                        {patient.healthInsuranceLogo && (
                                            <img src={patient.healthInsuranceLogo} alt="" style={{ width: 18, height: 18, borderRadius: 4, objectFit: 'cover' }} />
                                        )}
                                        {patient.healthInsuranceName}
                                    </>
                                ) : '—'}
                            </span>
                        </div>
                    </div>
                    <InfoRow icon={faCoins} label="Complémentaire N°" value={patient.complementaryNumber} />
                </SectionCard>

            </div>

            {/* ── Recent orders ── */}
            <SectionCard title="Dernières ventes" icon={faFileInvoice} className="pd-section--flush">
                <Table
                    mode="client"
                    columns={recentOrdersColumns}
                    data={recentOrders}
                    hoverable={true}
                    searchable={false}
                    emptyMessage="Aucune vente"
                    multiSelect={true}
                    enableCardView={true}
                    renderCard={renderOrderCard}
                    selectionActions={canCancel ? [
                        {
                            label: 'Annuler les ventes',
                            icon: faBan,
                            variant: 'danger',
                            onClick: (ids) => handleBulkCancel(ids),
                        },
                    ] : []}
                />
            </SectionCard>

            {/* ── Address + Map ── */}
            {defaultAddress && (
            <SectionCard title="Adresse" icon={faMapMarkerAlt}>
                {defaultAddress && (
                    <div className="pd-address-text">
                        <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" style={{ color: '#9ca3af' }} />
                        {[defaultAddress.addressLine1, defaultAddress.addressLine2, defaultAddress.city, defaultAddress.postalCode, defaultAddress.country]
                            .filter(Boolean).join(', ')}
                    </div>
                )}
                {/* the map only for an address explicitly marked as default */}
                {defaultAddress?.default && (
                    <div className="pd-map">
                        <LazyAddressMap address={[defaultAddress.addressLine1, defaultAddress.postalCode, defaultAddress.city, defaultAddress.country].filter(Boolean).join(' ')} />
                    </div>
                )}
            </SectionCard>
            )}

        </div>

        <NewInvoiceModal
            order={selectedOrder}
            show={showNewInvoiceModal}
            onClose={() => setShowNewInvoiceModal(false)}
            onSuccess={refreshPatientData}
        />
        <CreateReturnModal
            order={selectedOrder}
            show={showCreateReturnModal}
            onClose={() => setShowCreateReturnModal(false)}
            onSave={refreshPatientData}
        />
        <ConfirmModal
            show={showCancelConfirm}
            title={cancelIds.length > 1 ? `Annuler ${cancelIds.length} ventes` : 'Annuler la vente'}
            description={cancelIds.length > 1
                ? `Êtes-vous sûr de vouloir annuler ces ${cancelIds.length} ventes ? Cette action est irréversible.`
                : "Êtes-vous sûr de vouloir annuler cette vente ? Cette action est irréversible."
            }
            confirmLabel={cancelIds.length > 1 ? `Annuler les ${cancelIds.length} ventes` : 'Annuler la vente'}
            cancelLabel="Retour"
            confirmVariant="danger"
            onClose={() => { setShowCancelConfirm(false); setCancelIds([]); }}
            onConfirm={handleConfirmCancel}
        />
        <PatientDebtModal
            show={showDebtModal}
            onClose={() => setShowDebtModal(false)}
            patientId={patient.id}
            totalDue={patient.remainingToPay}
        />
        </>
    );
};

export default PatientDashboard;
